@let currentItem = playlistService.currentItem();
@let currentPlaylist = playlistService.currentPlaylist();
@let borderColor = currentItem?.theme?.textColor ?? currentPlaylist?.theme?.textColor ?? 'var(--color-base-300)';
@let textColor = currentItem?.theme?.textColor ?? currentPlaylist?.theme?.textColor ?? 'var(--color-base-content)';
@let backgroundColor = currentItem?.theme?.backgroundColor ?? currentPlaylist?.theme?.backgroundColor ?? 'var(--color-base-100)';

@if (currentItem && currentItem.mediaType === 'video') {
	<div
		name="floating-video-player"
		class="fixed right-4 bottom-28 z-50 aspect-video w-[min(24rem,calc(100vw-2rem))] overflow-hidden rounded-xl border border-black/20 bg-black shadow-2xl"
		#floatingVideoPlayer
	>
		<button
			type="button"
			name="floating-video-fullscreen"
			class="absolute right-12 top-2 z-20 rounded-full bg-black/60 p-1.5 text-white shadow transition-colors hover:bg-black/80"
			[attr.aria-label]="isFloatingVideoFullscreen() ? 'Exit fullscreen' : 'Enter fullscreen'"
			(click)="toggleFloatingVideoFullscreen(); $event.stopPropagation()"
		>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				class="block h-4 w-4"
				fill="none"
				viewBox="0 0 24 24"
				stroke="currentColor"
				stroke-width="2"
			>
				@if (isFloatingVideoFullscreen()) {
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						d="M9 9H5m0 0V5m0 4 5-5m5 5h4m0 0V5m0 4-5-5M9 15H5m0 0v4m0-4 5 5m5-5h4m0 0v4m0-4-5 5"
					/>
				} @else {
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						d="M4 9V4h5M4 4l6 6m10-1V4h-5m5 0-6 6M4 15v5h5m-5 0 6-6m10 1v5h-5m5 0-6-6"
					/>
				}
			</svg>
		</button>
		<button
			type="button"
			name="floating-video-close"
			class="absolute right-2 top-2 z-20 rounded-full bg-black/60 p-1.5 text-white shadow transition-colors hover:bg-black/80"
			[attr.aria-label]="'Close player'"
			(click)="stopAndClose(); $event.stopPropagation()"
		>
			<tw-cross class="block h-4 w-4"></tw-cross>
		</button>
		<button
			type="button"
			name="floating-video-toggle"
			class="absolute inset-0 z-10 cursor-pointer"
			[attr.aria-label]="playlistService.currentPlayingState() === 'play' ? 'Pause video' : 'Play video'"
			(click)="playlistService.togglePlayPause()"
		></button>
		<div
			class="h-full w-full"
			#floatingVideoContainer
		></div>
	</div>
}

<section
	name="root-player"
	[style.height]="'100px'"
	class="relative flex flex-row flex-nowrap items-center border-t shadow-[0_-2px_10px_-2px_rgba(0,0,0,0.12)]"
	[style.borderColor]="borderColor"
	[style.color]="textColor"
	[style.backgroundColor]="backgroundColor"
>
	<!-- column1 ("song title") -->
	@if (currentItem && currentItem.title) {
		<div
			name="column1"
			class="hidden pl-2 sm:block"
		>
			<span class="block w-40 truncate text-lg font-bold"> {{ currentItem.title }} </span>
		</div>
	}

	<!-- column2 ("the real playbar")-->
	<div
		name="column2"
		class="mx-auto w-full max-w-2xl flex-1 px-4"
	>
		<!-- buttons (next/play|pause/next /shuffle/loop...) -->
		<div
			name="btn-controls"
			class="mb-2 flex w-full select-none flex-row flex-nowrap gap-4"
		>
			<!-- shuffle and prev buttons -->
			<div
				name="btn-controls-left"
				class="flex flex-1 justify-end gap-2"
			>
				<!-- playback speed button -->
				<button
					name="speed-btn"
					class="hidden rounded px-1.5 text-xs font-bold tabular-nums sm:block"
					[attr.aria-label]="'Playback speed'"
					(click)="cyclePlaybackRate()"
				>
					{{ playlistService.playbackRate() }}x
				</button>
				<!-- shuffle button -->
				@if (displayShuffleButton()) {
					<button
						name="shuffle-btn"
						class="p-2"
					>
						<span class="flex">
							<svg
								role="img"
								aria-hidden="true"
								viewBox="0 0 16 16"
								class="h-4 w-4 fill-current"
							>
								<path d="M13.151.922a.75.75 0 1 0-1.06 1.06L13.109 3H11.16a3.75 3.75 0 0 0-2.873 1.34l-6.173 7.356A2.25 2.25 0 0 1 .39 12.5H0V14h.391a3.75 3.75 0 0 0 2.873-1.34l6.173-7.356a2.25 2.25 0 0 1 1.724-.804h1.947l-1.017 1.018a.75.75 0 0 0 1.06 1.06L15.98 3.75 13.15.922zM.391 3.5H0V2h.391c1.109 0 2.16.49 2.873 1.34L4.89 5.277l-.979 1.167-1.796-2.14A2.25 2.25 0 0 0 .39 3.5z"></path>
								<path d="m7.5 10.723.98-1.167.957 1.14a2.25 2.25 0 0 0 1.724.804h1.947l-1.017-1.018a.75.75 0 1 1 1.06-1.06l2.829 2.828-2.829 2.828a.75.75 0 1 1-1.06-1.06L13.109 13H11.16a3.75 3.75 0 0 1-2.873-1.34l-.787-.938z"></path>
							</svg>
						</span>
					</button>
				}
				<!-- prev button -->
				@if (displayPrevButton()) {
					<button
						name="prev-btn"
						class="p-2"
						(click)="prev()"
					>
						<span class="flex">
							<svg
								role="img"
								aria-hidden="true"
								viewBox="0 0 16 16"
								class="h-4 w-4 fill-current"
							>
								<path d="M3.3 1a.7.7 0 0 1 .7.7v5.15l9.95-5.744a.7.7 0 0 1 1.05.606v12.575a.7.7 0 0 1-1.05.607L4 9.149V14.3a.7.7 0 0 1-.7.7H1.7a.7.7 0 0 1-.7-.7V1.7a.7.7 0 0 1 .7-.7h1.6z"></path>
							</svg>
						</span>
					</button>
				}
			</div>

			<!-- skip backward button -->
			<button
				name="skip-backward-btn"
				class="hidden items-center gap-0.5 p-2 text-xs font-semibold tabular-nums sm:flex"
				[attr.aria-label]="'Skip backward ' + skipSeconds + ' seconds'"
				(click)="skipBackward()"
			>
				<span class="flex">
					<svg
						role="img"
						aria-hidden="true"
						viewBox="0 0 16 16"
						class="h-4 w-4 fill-current"
					>
						<path d="M8 3V1L4.5 4 8 7V5a4 4 0 1 1-4 4H2.5a5.5 5.5 0 1 0 5.5-6z"></path>
					</svg>
					{{ skipSeconds }}
				</span>
			</button>

			<!-- play/pause button -->
			<button
				name="toggle-play-pause"
				class="rounded-full p-2"
				[style.color]="backgroundColor"
				[style.backgroundColor]="textColor"
				(click)="playlistService.togglePlayPause()"
			>
				<span class="flex">
					@if (playlistService.currentPlayingState() === 'play') {
						<svg
							role="img"
							aria-hidden="true"
							viewBox="0 0 16 16"
							class="h-4 w-4 fill-current"
						>
							<path d="M2.7 1a.7.7 0 0 0-.7.7v12.6a.7.7 0 0 0 .7.7h2.6a.7.7 0 0 0 .7-.7V1.7a.7.7 0 0 0-.7-.7H2.7zm8 0a.7.7 0 0 0-.7.7v12.6a.7.7 0 0 0 .7.7h2.6a.7.7 0 0 0 .7-.7V1.7a.7.7 0 0 0-.7-.7h-2.6z"></path>
						</svg>
					} @else {
						<svg
							role="img"
							aria-hidden="true"
							viewBox="0 0 16 16"
							class="h-4 w-4 fill-current"
						>
							<path d="M3 1.713a.7.7 0 0 1 1.05-.607l10.89 6.288a.7.7 0 0 1 0 1.212L4.05 14.894A.7.7 0 0 1 3 14.288V1.713z"></path>
						</svg>
					}
				</span>
			</button>

			<!-- skip forward button -->
			<button
				name="skip-forward-btn"
				class="hidden items-center gap-0.5 p-2 text-xs font-semibold tabular-nums sm:flex"
				[attr.aria-label]="'Skip forward ' + skipSeconds + ' seconds'"
				(click)="skipForward()"
			>
				<span class="flex">
					<svg
						role="img"
						aria-hidden="true"
						viewBox="0 0 16 16"
						class="h-4 w-4 fill-current"
					>
						<path d="M8 3V1l3.5 3L8 7V5a4 4 0 1 0 4 4h1.5A5.5 5.5 0 1 1 8 3z"></path>
					</svg>
					{{ skipSeconds }}
				</span>
			</button>

			<!-- next and repeat buttons -->
			<div
				name="btn-controls-right"
				class="flex flex-1 justify-start gap-2"
			>
				<!-- next button -->
				@if (displayNextButton()) {
					<button
						name="next-btn"
						class="p-2"
						(click)="next()"
					>
						<span class="flex">
							<svg
								role="img"
								aria-hidden="true"
								viewBox="0 0 16 16"
								class="h-4 w-4 fill-current"
							>
								<path d="M12.7 1a.7.7 0 0 0-.7.7v5.15L2.05 1.107A.7.7 0 0 0 1 1.712v12.575a.7.7 0 0 0 1.05.607L12 9.149V14.3a.7.7 0 0 0 .7.7h1.6a.7.7 0 0 0 .7-.7V1.7a.7.7 0 0 0-.7-.7h-1.6z"></path>
							</svg>
						</span>
					</button>
				}
				<!-- repeat button -->
				@if (displayRepeatButton()) {
					<button
						name="repeat-btn"
						class="p-2"
					>
						<span class="flex">
							<svg
								role="img"
								aria-hidden="true"
								viewBox="0 0 16 16"
								class="h-4 w-4 fill-current"
							>
								<path d="M0 4.75A3.75 3.75 0 0 1 3.75 1h8.5A3.75 3.75 0 0 1 16 4.75v5a3.75 3.75 0 0 1-3.75 3.75H9.81l1.018 1.018a.75.75 0 1 1-1.06 1.06L6.939 12.75l2.829-2.828a.75.75 0 1 1 1.06 1.06L9.811 12h2.439a2.25 2.25 0 0 0 2.25-2.25v-5a2.25 2.25 0 0 0-2.25-2.25h-8.5A2.25 2.25 0 0 0 1.5 4.75v5A2.25 2.25 0 0 0 3.75 12H5v1.5H3.75A3.75 3.75 0 0 1 0 9.75v-5z"></path>
							</svg>
						</span>
					</button>
				}

				<!-- volume control -->
				<div
					name="volume-control"
					class="hidden items-center gap-1 sm:flex"
				>
					<button
						name="mute-btn"
						class="p-2"
						[attr.aria-label]="isSilent() ? 'Unmute' : 'Mute'"
						(click)="toggleMute()"
					>
						<span class="flex">
							<svg
								role="img"
								aria-hidden="true"
								viewBox="0 0 16 16"
								class="h-4 w-4 fill-current"
							>
								<path d="M8 3.4a.5.5 0 0 0-.82-.38L4.6 5H2.5A1.5 1.5 0 0 0 1 6.5v3A1.5 1.5 0 0 0 2.5 11h2.1l2.58 1.98A.5.5 0 0 0 8 12.6V3.4z"></path>
								@if (isSilent()) {
									<path
										d="M11 6l3 4m0-4l-3 4"
										stroke="currentColor"
										stroke-width="1.2"
										fill="none"
										stroke-linecap="round"
									></path>
								} @else {
									<path
										d="M10.5 5.5a3.5 3.5 0 0 1 0 5"
										stroke="currentColor"
										stroke-width="1.2"
										fill="none"
										stroke-linecap="round"
									></path>
								}
							</svg>
						</span>
					</button>
					<input
						type="range"
						name="volume-slider"
						class="range range-xs w-16"
						min="0"
						max="1"
						step="0.05"
						[attr.aria-label]="'Volume'"
						[value]="isSilent() ? 0 : playlistService.volume()"
						(input)="onVolumeInput($event)"
					/>
				</div>
			</div>
		</div>
		<!-- seekbar (current time, bar, duration) -->
		<lib-seek-bar
			[currentTime]="playlistService.currentTime()"
			[duration]="playlistService.currentItemDuration()"
			[bufferedEnd]="playlistService.bufferedSeconds()"
			[mediaUrl]="waveformMediaUrl()"
			[loading]="isLoading()"
			[hasError]="playlistService.hasError()"
			[trackColor]="'color-mix(in srgb, ' + textColor + ' 25%, transparent)'"
			[progressColor]="textColor"
			[thumbColor]="textColor"
			[bufferedColor]="textColor"
			[contentColor]="textColor"
			(seek)="onSeek($event)"
		/>
	</div>

	<!-- close -->
	<div class="relative h-full w-10 shrink-0 overflow-hidden">
		<div
			name="close"
			class="absolute right-0 top-0"
			[style.color]="textColor"
		>
			<button
				class="p-2"
				(click)="stopAndClose()"
			>
				<tw-cross class="block h-5 w-5"></tw-cross>
			</button>
		</div>
	</div>
</section>
